import React, { FunctionComponent } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';

import ProtectedRoute from 'components/ProtectedRoute';
import PostOnboardingInterstitial from 'components/Interstitials/Tier3/Success/PostOnboarding';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { useGetMerchantVenueCounts } from 'helpers/useMerchantVenueCounts';

import MerchantStatusDetails from 'scenes/Home/scenes/MerchantStatusDetails';
import NotFound from 'scenes/NotFound';
import CSVExport from 'scenes/CSVExport';
import Reports from 'scenes/Report';
import ReportDetail from 'scenes/Report/scenes/ReportDetail';
import BatchReport from 'scenes/Report/scenes/BatchReport';
import AccountManagement from 'scenes/AccountManagement';

import {
  reportsTargetingRules,
  ReportRules,
  reportsConfig,
} from 'scenes/Report/Report.mock';

const externalRoutes = (config: ReportRules) => [
  { path: '/csv-export', component: CSVExport },
  {
    path: '/:merchantName/account-management',
    component: AccountManagement,
  },
  {
    path: '/:merchantName/success/onboarding',
    component: PostOnboardingInterstitial,
    exact: true,
  },
  {
    config,
    path: '/:merchantName/reports',
    targetingRules: reportsTargetingRules,
    exact: true,
    render: () => <Reports {...config} />,
  },
  {
    path: '/:merchantName/reports/detail',
    targetingRules: reportsTargetingRules,
    render: () => <ReportDetail {...config} />,
  },
  {
    path: '/:merchantName/reports/batch-report',
    targetingRules: reportsTargetingRules,
    render: () => <BatchReport {...config} />,
  },

  { path: '/:merchantName', component: MerchantStatusDetails },
];

const renderExternalRoutes = (merchantName: string) => {
  const merchantConfig = reportsConfig[merchantName];

  return externalRoutes(
    merchantConfig,
  ).map(({ targetingRules = () => true, ...props }, index) =>
    targetingRules(merchantName) ? (
      <ProtectedRoute key={index} {...props} />
    ) : null,
  );
};

const ExternalRoutes: FunctionComponent = () => {
  const { venueCounts, loading: loadingVenues } = useGetMerchantVenueCounts();
  const { isTier3, merchantName } = useUserMgmt();

  if (loadingVenues) return null;

  const renderRedirect = () => <Redirect to={`/${merchantName}/overview`} />;

  return (
    <>
      <Switch>
        <Route
          exact
          path={['/', '/login', '/reset-password']}
          render={renderRedirect}
        />

        {renderExternalRoutes(merchantName)}

        <Route component={NotFound} />
      </Switch>
      {isTier3 && !loadingVenues && venueCounts === 0 && (
        <Redirect to={`/${merchantName}/venue/onboarding`} />
      )}
    </>
  );
};

export default ExternalRoutes;
